import { removeObjectFromCanvas } from '@frontend/utils';
import { makeBackgroundColorRectangle } from '@frontend/draw_fields_template/background_color';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { getImage } from '@frontend/constants/image-calculations';
import { fabric } from 'fabric';
import TemplateService from '@frontend/services/api/template';
import { getBackgroundColorGeometry } from './background-color-geometry';

const getScopePage = () => ShowPageScopeCollector.get(['updateLayoutChanges']);

export async function drawBackgroundColor({ fields, canvas, data, dimension, template }) {
  let backgroundColorField = fields.find(f => f.type === 'Background Color');

  if (!backgroundColorField) {
    let backResponse = await TemplateService.createBackgroundColorField(data.template_id);
    if (!backResponse || !backResponse?.data) return;
    backgroundColorField = backResponse.data;
  }

  const backgroundColorFieldOptions = JSON.parse(backgroundColorField.options);

  const { updateLayoutChanges } = getScopePage();

  removeObjectFromCanvas(canvas, backgroundColorField.element_id);

  const geometry = getBackgroundColorGeometry(
    backgroundColorFieldOptions,
    dimension || [canvas.width, canvas.height],
    template
  );

  const initialWidth = geometry.width;
  const initialHeight = geometry.height;

  const backgroundColorRectangle = makeBackgroundColorRectangle({
    fill: 'transparent',
    width: initialWidth,
    height: initialHeight,
    fieldId: backgroundColorField.id,
  }, backgroundColorField,);

  let settings = {};

  backgroundColorRectangle.isSettingsChanged = function (settings) {
    return this.backupData !== JSON.stringify(settings);
  };

  if (Object.hasOwn(data, backgroundColorRectangle.id)) {
    settings = { ...data[backgroundColorRectangle.id] };
    backgroundColorRectangle.backupData = JSON.stringify(data[backgroundColorRectangle.id]);
  }
  // commented in accordance with the requirements of ticket POR-11951
  // const resolvedWidth = parseInt(settings.width) || initialWidth;
  // const resolvedHeight = parseInt(settings.height) || initialHeight;
  
  //it`s should use width and height from base field (See POR-11951).
  const resolvedWidth =  initialWidth;
  const resolvedHeight =  initialHeight;
  if (resolvedWidth !== backgroundColorRectangle.width || resolvedHeight !== backgroundColorRectangle.height) {
    backgroundColorRectangle.set({ width: resolvedWidth, height: resolvedHeight });
  }

  backgroundColorRectangle.updateSettings = function (settings) {
    if (!this.isSettingsChanged(settings)) return;
    this.backupData = JSON.stringify(settings);
    updateLayoutChanges(canvas.instance_id, backgroundColorRectangle.id, settings);
  };

  backgroundColorRectangle.editor.applySettings(settings);

  // Background settings may still contain the source size. Rebuild the pattern
  // at the resolved field size so it covers the resized canvas after reload.
  backgroundColorRectangle.editor.canvas.width = resolvedWidth;
  backgroundColorRectangle.editor.canvas.height = resolvedHeight;
  backgroundColorRectangle.editor.redraw();

  // Baseline change detection on the editor's own serialization of the loaded state. The
  // sidebar's BackgroundColorEditor calls redraw() -> updateSettings() while (re)initializing
  // for a newly selected template; comparing against the raw stored JSON reads formatting
  // differences as a change and records phantom unsaved layoutChanges.
  backgroundColorRectangle.backupData = JSON.stringify(backgroundColorRectangle.editor.getSettings());

  try {
    const dataURL = backgroundColorRectangle.editor.canvas.toDataURL();

    if (!dataURL || dataURL === 'data:,') {
      console.warn('Background color canvas generated empty data URL, adding rectangle without pattern');
      canvas.add(backgroundColorRectangle);
      return;
    }

    await getImage(dataURL)
      .then((img) => {
        backgroundColorRectangle.fill = new fabric.Pattern({
          source: img,
          repeat: 'no-repeat'
        });

        canvas.add(backgroundColorRectangle);
      })
      .catch((error) => {
        console.error('Failed to load background color image:', error);
        canvas.add(backgroundColorRectangle);
      });
  } catch (error) {
    console.error('Failed to generate background color data URL:', error);
    canvas.add(backgroundColorRectangle);
  }
}
